import { useMemo } from 'react'
import { useListDepreciationsQuery } from '@api/depreciation/depreciationEndpointsHooks'
import { useGetSegmentByKeyQuery } from '@api/segment/segmentEndpointsHooks'

type IdDescMap = Record<string, string>

export interface AssetLookupMaps {
  categoryMap: IdDescMap
  taxClassMap: IdDescMap
  segmentMaps: Record<number, IdDescMap>
  isLoading: boolean
}

const EMPTY: IdDescMap = {}

const buildDeprecMap = (list?: { ID: string; Name: string }[]): IdDescMap => {
  if (!list) return EMPTY
  const map: IdDescMap = {}
  for (const item of list) map[item.ID] = item.Name
  return map
}

const buildSegmentMap = (list?: { KY_VALUE: string; KY_DESC: string }[]): IdDescMap => {
  if (!list) return EMPTY
  const map: IdDescMap = {}
  for (const item of list) map[item.KY_VALUE] = item.KY_DESC
  return map
}

const useAssetLookupMaps = (): AssetLookupMaps => {
  const { data: categories, isLoading: lCat } = useListDepreciationsQuery({ profile: 'A' })
  const { data: taxClasses, isLoading: lTcls } = useListDepreciationsQuery({ profile: 'X' })

  const { data: seg0, isLoading: lS0 } = useGetSegmentByKeyQuery({ KY_NR: 0 })
  const { data: seg1, isLoading: lS1 } = useGetSegmentByKeyQuery({ KY_NR: 1 })
  const { data: seg2, isLoading: lS2 } = useGetSegmentByKeyQuery({ KY_NR: 2 })
  const { data: seg3, isLoading: lS3 } = useGetSegmentByKeyQuery({ KY_NR: 3 })
  const { data: seg4, isLoading: lS4 } = useGetSegmentByKeyQuery({ KY_NR: 4 })
  const { data: seg5, isLoading: lS5 } = useGetSegmentByKeyQuery({ KY_NR: 5 })
  const { data: seg6, isLoading: lS6 } = useGetSegmentByKeyQuery({ KY_NR: 6 })
  const { data: seg7, isLoading: lS7 } = useGetSegmentByKeyQuery({ KY_NR: 7 })
  const { data: seg8, isLoading: lS8 } = useGetSegmentByKeyQuery({ KY_NR: 8 })
  const { data: seg9, isLoading: lS9 } = useGetSegmentByKeyQuery({ KY_NR: 9 })

  const categoryMap = useMemo(() => buildDeprecMap(categories), [categories])
  const taxClassMap = useMemo(() => buildDeprecMap(taxClasses), [taxClasses])

  const segmentMaps = useMemo<Record<number, IdDescMap>>(
    () => ({
      0: buildSegmentMap(seg0),
      1: buildSegmentMap(seg1),
      2: buildSegmentMap(seg2),
      3: buildSegmentMap(seg3),
      4: buildSegmentMap(seg4),
      5: buildSegmentMap(seg5),
      6: buildSegmentMap(seg6),
      7: buildSegmentMap(seg7),
      8: buildSegmentMap(seg8),
      9: buildSegmentMap(seg9),
    }),
    [seg0, seg1, seg2, seg3, seg4, seg5, seg6, seg7, seg8, seg9]
  )

  const isLoading =
    lCat || lTcls || lS0 || lS1 || lS2 || lS3 || lS4 || lS5 || lS6 || lS7 || lS8 || lS9

  return { categoryMap, taxClassMap, segmentMaps, isLoading }
}

export default useAssetLookupMaps
